import * as amplitude from '@amplitude/analytics-browser';
import * as Sentry from '@sentry/react';
import { UserType } from '@inspectify/core';
import { getTreatments } from '@splitsoftware/splitio-redux';
import {
  AuthUser,
  confirmResetPassword,
  fetchAuthSession,
  getCurrentUser as getAmplifyUser,
  resetPassword,
  ResetPasswordOutput,
  signIn,
  SignInInput,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';
import { Hub } from "aws-amplify/utils";
import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useLazyGetCurrentUserQuery as useLazyGetInspectifyUserQuery } from '../gql/generated/queries';
import { snackActions } from '../redux/slices/snackSlice/snack';
import { useAppDispatch } from '../redux/store';
import { LocalStorageKey } from '../vendor/local';
import { captureException } from '../vendor/sentry';
import { splitNames } from '../vendor/splitio';
import { UnknownAction } from '@reduxjs/toolkit';
import { NavigateFunction, useParams } from 'react-router-dom';
import { clearMfa } from 'utils/mfa';

export type AppUser = {
  user?: UserType;
  currentContractor?: UserType;
  availableUsers?: UserType[];
  challengeName?: string;
}

interface IAuthProvider {
  userContext: AppUser;

  isReady: boolean;

  setSelectedUserContractor(user: UserType): void;

  forgot(email: string): Promise<ResetPasswordOutput | undefined>;

  login(input: SignInInput, navigate: NavigateFunction): Promise<AppUser | undefined>;

  logout(): Promise<void>;

  reset(email: string, password: string, code: string): Promise<void>;

  googleSignIn(from: string): Promise<void>;
}

export const AuthContext = createContext<IAuthProvider>(null!);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Needed in development due to strict mode running useEffects twice
  const authInitialized = useRef(false);

  const [userContext, setUser] = useState<AppUser>({});
  const [isReady, setIsReady] = useState(false);

  const [getInspectifyUser] = useLazyGetInspectifyUserQuery();
  const dispatch = useAppDispatch();
  const { isu: inspectifySelectedUser } = useParams();

  const setSelectedUserContractor = async (currentContractor: UserType | undefined) => {
    if (!currentContractor) return;

    setIsReady(false)
    localStorage.setItem(LocalStorageKey.SelectedContractor, currentContractor.id as string);

    const { data: newData } = await getInspectifyUser()

    const user = {
      user: newData?.currentUser as UserType,
      currentContractor,
      availableUsers: newData?.availableUsers as UserType[]
    }

    setUser(user);
    setIsReady(true)
  }

  const initAuth = async () => {
    let amplifyUser;

    try {
      // Check if a user exists. If not, Amplify throws.
      amplifyUser = await getAmplifyUser();
    } catch {
      // There is no logged in user.
      setIsReady(true);
      return undefined;
    }

    try {
      // There is a logged in amplify user. Fetch and refresh the session then complete login.
      await fetchSession();

      const user = await completeLogin(amplifyUser);

      setIsReady(true);
      return user;
    } catch (e) {
      dispatch(snackActions.pushError({ name: 'Authentication', message: 'Error fetching user data' }));
      captureException(e, { source: 'initAuth' });

      setIsReady(true);
      return undefined;
    }
  }

  const completeLogin = async (authUser?: AuthUser) => {
    if (!authUser) return undefined;

    const { data: newData, isError } = await getInspectifyUser();

    if (isError || !newData) {
      await signOut();
      throw new Error('Error fetching user data');
    }

    const lastSelectedContractorUserId = inspectifySelectedUser || localStorage.getItem(LocalStorageKey.SelectedContractor);
    const currentContractor: UserType = newData.availableUsers
      ?.find(user => user.id === lastSelectedContractorUserId) as UserType
      || newData.availableUsers?.[0] as UserType;

    if (!newData.currentUser || !currentContractor) return undefined;

    const identifyEvent = new amplitude.Identify();
    identifyEvent.set('email', newData?.currentUser?.email || '');
    identifyEvent.set('contractorId', newData?.currentUser?.contractor?.id || '');
    identifyEvent.set('cognitoId', authUser.userId);
    amplitude.setGroup('contractorType', newData?.currentUser?.contractor?.contractorType || '');
    amplitude.setGroup('teamId', newData?.currentUser?.teamUsers?.[0]?.team?.id || '');
    amplitude.identify(identifyEvent);

    Sentry.setUser({ email: newData?.currentUser?.email || '' });

    const user = {
      user: newData.currentUser as UserType,
      currentContractor,
      availableUsers: newData.availableUsers as UserType[],
    };

    setUser(user);
    localStorage.setItem(LocalStorageKey.SelectedContractor, currentContractor.id as string);
    dispatch(getTreatments({ splitNames }) as UnknownAction);

    return user;
  };

  // Set auth event listeners and init auth
  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      clearMfa();

      switch (payload.event) {
        case "signInWithRedirect":
          initAuth();
          break;
        case "signInWithRedirect_failure":
          dispatch(snackActions.pushError({
            message: "An error has occurred during the OAuth flow.",
            name: "Authentication",
            severity: "error"
          }));
          break;
        case "customOAuthState":
          // using localStorage to store the sso redirect url because the page is refreshed after the sso redirect
          // losing the state of the app
          localStorage.setItem(LocalStorageKey.SsoRedirect, payload.data);
          break;
      }
    });

    if (!authInitialized.current) {
      authInitialized.current = true;

      initAuth();
    }

    return unsubscribe;
  }, []);

  const login = async (newUser: SignInInput, navigate: NavigateFunction) => {
    const output = await signIn(newUser);

    if (output.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      navigate('/temp-password', { state: { email: newUser.username } });
      return;
    }

    return await initAuth();
  }

  const forgot = async (email: string) => {
    try {
      return resetPassword({ username: email });
    } catch (error) {
      dispatch(snackActions.pushError({ name: 'Authentication', message: 'Error fetching user data' }));
      captureException(error, { source: 'forgotPassword' });
    }
  }

  const reset = async (email: string, password: string, confirmationCode: string) => {
    try {
      await confirmResetPassword({ confirmationCode, options: undefined, username: email, newPassword: password });
    } catch (error) {
      dispatch(snackActions.pushError({ name: 'Authentication', message: 'Error fetching user data' }));
      captureException(error, { source: 'resetPassword' });
    }
  }

  const googleSignIn = async (from: string) => {
    try {
      return signInWithRedirect({ provider: 'Google', customState: from || '/' });
    } catch (error) {
      dispatch(snackActions.pushError({ name: 'Authentication', message: 'Error fetching user data' }));
      captureException(error, { source: 'googleSignIn' });
    }
  }

  const logout = async () => {
    localStorage.removeItem(LocalStorageKey.SelectedContractor);
    localStorage.removeItem(LocalStorageKey.TokenExp);
    localStorage.removeItem(LocalStorageKey.Token);
    clearMfa();
    setUser({});
    amplitude.reset();
    Sentry.setUser(null);
    return signOut();
  };

  const value = {
    userContext,
    isReady,
    login,
    logout,
    forgot,
    reset,
    googleSignIn,
    setSelectedUserContractor,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * https://docs.amplify.aws/gen1/react/build-a-backend/auth/manage-user-session/
 */
export const fetchSession = async (forceRefresh = false) => {
  try {
    const session = await fetchAuthSession({ forceRefresh });
    const { idToken } = session.tokens ?? {};
    // debugger
    localStorage.setItem(LocalStorageKey.Token, idToken?.toString() ?? '');
    localStorage.setItem(LocalStorageKey.TokenExp, ((idToken?.payload?.exp || 0) * 1000).toString());
  } catch (error) {
    captureException(error, { source: 'fetchSession' });
  }
}
